import { useEffect, useRef, useState } from 'react';
import Modal, { Field } from '../../components/Modal.jsx';
import { get, post } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.jsx';
import { formatMoney } from '../../lib/format.js';
import { errorMessage } from '../../lib/errors.js';
import { toast } from '../../lib/toast.jsx';
import { domainErrorText, validateDomain } from '../../lib/domain.js';
import { useT } from '../../lib/i18n.jsx';
import RegistrantFields, {
  errorText,
  fieldErrorsFrom422,
  initialRegistrant,
  missingFields,
  rememberRegistrant,
} from './RegistrantFields.jsx';

// Step 1 checks the name (GET /dns/availability, 20/min). Then:
// - free → order a registration: price, period, holder → POST /dns/domains/order
//   → Mollie checkout. Nothing is registered before payment.
// - registered → either link it (the customer owns it: POST /dns/domains, free) or
//   transfer it to us (EPP code + holder → POST /dns/domains/order, paid).
// After Mollie the customer lands on /domeinen?order=<id> (DomainOrders.jsx).
const UNAVAILABLE = ['registered', 'reserved', 'premium', 'invalid', 'unsupported_tld', 'unknown'];

export default function AddDomainModal({ lang, existing = [], onClose, onCreated, onOrdersChanged }) {
  const t = useT();
  const { user } = useAuth();
  const [input, setInput] = useState('');
  const [touched, setTouched] = useState(false);
  const [step, setStep] = useState('enter'); // 'enter' | 'register' | 'registered'
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null); // response of /dns/availability
  const [tlds, setTlds] = useState(null);
  const [years, setYears] = useState(1);
  const [choice, setChoice] = useState('link'); // for a registered name: 'link' | 'transfer'
  const [authCode, setAuthCode] = useState('');
  const [registrant, setRegistrant] = useState(() => initialRegistrant(user));
  const [fieldErrors, setFieldErrors] = useState({});
  const primaryRef = useRef(null);

  useEffect(() => {
    let alive = true;
    get('/dns/tlds')
      .then((list) => alive && setTlds(Array.isArray(list) ? list : []))
      .catch(() => alive && setTlds(null)); // without the list, availability still decides
    return () => {
      alive = false;
    };
  }, []);

  const result = validateDomain(input);
  // Only a name under an extension we sell can be registered.
  const knownTld = !result.ok || !tlds?.length || tlds.some((x) => result.domain.endsWith(String(x.extension).toLowerCase()));
  const duplicate = result.ok && existing.some((d) => d.toLowerCase() === result.domain);
  const fieldError = !touched
    ? ''
    : !result.ok
      ? domainErrorText(result.code, lang)
      : duplicate
        ? t('addDomain.duplicate')
        : !knownTld
          ? t('addDomain.status.unsupported_tld')
          : '';
  const domain = availability?.domain || (result.ok ? result.domain : '');
  const tldInfo = (tlds || []).find((x) => x.extension === availability?.tld) || null;

  const changeInput = (v) => {
    setInput(v);
    setAvailability(null);
    setErr('');
  };

  const goBack = () => {
    setStep('enter');
    setErr('');
    setFieldErrors({});
  };

  const next = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!result.ok || duplicate || !knownTld || checking) return;
    setErr('');
    setChecking(true);
    try {
      const res = await get(`/dns/availability?domain=${encodeURIComponent(result.domain)}`);
      setAvailability(res);
      setFieldErrors({});
      if (res?.available) {
        setYears(res.min_years || 1);
        setStep('register');
      } else if (res?.status === 'registered') {
        setChoice('link');
        setStep('registered');
      }
    } catch (e2) {
      setAvailability(null);
      setErr(e2?.status === 400 ? t('addDomain.status.invalid') : errorMessage(e2, { lang }));
    } finally {
      setChecking(false);
    }
  };

  const orderErrorText = (e2) => {
    const detail = e2?.payload?.detail;
    const code = detail && typeof detail === 'object' && !Array.isArray(detail) ? detail.code : null;
    if (code === 'not_available') {
      const s = UNAVAILABLE.includes(detail.availability) ? detail.availability : 'unknown';
      return t(`addDomain.status.${s}`, { domain });
    }
    if (code === 'availability_unknown') return t('addDomain.status.unknown', { domain });
    if (code === 'not_registered') return t('order.err.notRegistered', { domain });
    if (code === 'domain_exists') return t('addDomain.exists');
    if (code === 'already_managed') return t('order.err.alreadyManaged', { domain });
    if (code === 'order_exists') return t('order.err.orderExists', { domain });
    if (code === 'payment_failed') return t('order.err.paymentFailed');
    if (code === 'price_unavailable') return t('order.err.priceUnavailable');
    return errorMessage(e2, { lang });
  };

  const placeOrder = async (action) => {
    const errors = missingFields(registrant);
    if (action === 'transfer' && !authCode.trim()) errors.auth_code = 'required';
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setErr(t('order.err.fields'));
      return;
    }
    setBusy(true);
    setErr('');
    try {
      const res = await post('/dns/domains/order', {
        domain_name: domain,
        action,
        period_years: action === 'register' ? years : undefined,
        auth_code: action === 'transfer' ? authCode.trim() : undefined,
        registrant,
      });
      rememberRegistrant(registrant);
      window.location.assign(res.checkout_url);
      // Keep the dialog busy while the browser leaves for Mollie.
    } catch (e2) {
      setBusy(false);
      if (e2?.status === 422) {
        const fe = fieldErrorsFrom422(e2?.payload?.detail);
        setFieldErrors(fe);
        const general = fe.domain_name || fe.action;
        setErr(general ? t('addDomain.status.invalid') : t('order.err.fields'));
        return;
      }
      if (e2?.status === 502 || e2?.payload?.detail?.code === 'order_exists') onOrdersChanged?.();
      setErr(orderErrorText(e2));
    }
  };

  const link = async () => {
    setBusy(true);
    setErr('');
    try {
      await post('/dns/domains', { domain_name: domain });
      toast(t('addDomain.linked', { domain }));
      onCreated?.(domain);
    } catch (e2) {
      setErr(
        e2?.status === 400 && /exist/i.test(e2?.message || '')
          ? t('addDomain.exists')
          : errorMessage(e2, { lang }),
      );
    } finally {
      setBusy(false);
    }
  };

  // A free name can be prepared first: its zone and records are made now (Bunny DNS), the
  // registration is ordered later and the domain works from the moment it is registered.
  const prestage = async () => {
    setBusy(true);
    setErr('');
    try {
      await post('/dns/domains', { domain_name: domain, prestage: true });
      toast(t('addDomain.prestaged', { domain }));
      onCreated?.(domain);
    } catch (e2) {
      setErr(
        e2?.status === 400 && /exist/i.test(e2?.message || '')
          ? t('addDomain.exists')
          : errorMessage(e2, { lang }),
      );
    } finally {
      setBusy(false);
    }
  };

  const close = busy ? () => {} : onClose;
  const backButton = (
    <button type="button" className="btn-ghost" disabled={busy} onClick={goBack}>
      {t('addDomain.back')}
    </button>
  );

  if (step === 'register' && availability?.available) {
    const min = availability.min_years || 1;
    const max = Math.max(min, availability.max_years || min);
    const price = availability.price_register_yearly;
    const renew = availability.price_renew_yearly;
    const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    return (
      <Modal
        onClose={close}
        maxWidth={560}
        initialFocusRef={primaryRef}
        title={t('addDomain.order.title', { domain })}
        description={t('addDomain.order.description')}
        footer={
          <>
            {backButton}
            <button type="button" className="btn-ghost" disabled={busy} onClick={prestage} title={t('addDomain.prestage.hint')}>
              {t('addDomain.prestage')}
            </button>
            <button ref={primaryRef} type="button" className="btn-primary" disabled={busy} onClick={() => placeOrder('register')}>
              {busy ? t('addDomain.redirecting') : t('addDomain.pay')}
            </button>
          </>
        }
      >
        <div className="modal-body">
          <div className="login-form">
            {err && <div className="login-error" role="alert">{err}</div>}
            <div className="confirm-box">
              <div className="kv">
                <span className="k">{t('dom.col.domain')}</span>
                <span className="v mono">{domain}</span>
              </div>
              {price != null && (
                <div className="kv">
                  <span className="k">{t('addDomain.pricePerYear')}</span>
                  <span className="v">{formatMoney(price, lang)}</span>
                </div>
              )}
              {renew != null && (
                <div className="kv">
                  <span className="k">{t('addDomain.renew')}</span>
                  <span className="v">{t('addDomain.renew.price', { amount: formatMoney(renew, lang) })}</span>
                </div>
              )}
            </div>
            <Field label={t('addDomain.term')} error={errorText(fieldErrors.period_years, t)}>
              <select value={years} disabled={busy || options.length === 1} onChange={(e) => setYears(Number(e.target.value))}>
                {options.map((n) => (
                  <option key={n} value={n}>
                    {price != null
                      ? t(n === 1 ? 'addDomain.period.price.one' : 'addDomain.period.price.other', {
                          n,
                          amount: formatMoney(price * n, lang),
                        })
                      : t(n === 1 ? 'addDomain.period.one' : 'addDomain.period.other', { n })}
                  </option>
                ))}
              </select>
            </Field>
            <RegistrantFields value={registrant} onChange={setRegistrant} errors={fieldErrors} lang={lang} disabled={busy} />
            <p className="field-hint" style={{ margin: 0 }}>{t('addDomain.vatNote')}</p>
            <p className="field-hint" style={{ margin: 0 }}>{t('addDomain.prestage.hint')}</p>
          </div>
        </div>
      </Modal>
    );
  }

  if (step === 'registered' && availability?.status === 'registered') {
    // 0 means the transfer is not priced yet (the API refuses such an order).
    const transferPrice = tldInfo?.price_transfer > 0 ? tldInfo.price_transfer : null;
    const options = [
      ['link', t('addDomain.choice.link'), t('addDomain.choice.link.sub')],
      [
        'transfer',
        t('addDomain.choice.transfer'),
        transferPrice != null
          ? t('addDomain.choice.transfer.sub', { amount: formatMoney(transferPrice, lang) })
          : t('addDomain.choice.transfer.unpriced'),
      ],
    ];
    return (
      <Modal
        onClose={close}
        maxWidth={560}
        initialFocusRef={primaryRef}
        title={t('addDomain.registered.title', { domain })}
        description={t('addDomain.registered.description')}
        footer={
          <>
            {backButton}
            {choice === 'link' ? (
              <button ref={primaryRef} type="button" className="btn-primary" disabled={busy} onClick={link}>
                {busy ? t('addDomain.working') : t('addDomain.link')}
              </button>
            ) : (
              <button ref={primaryRef} type="button" className="btn-primary" disabled={busy} onClick={() => placeOrder('transfer')}>
                {busy ? t('addDomain.redirecting') : t('addDomain.pay')}
              </button>
            )}
          </>
        }
      >
        <div className="modal-body">
          <div className="login-form">
            {err && <div className="login-error" role="alert">{err}</div>}
            <div role="radiogroup" aria-label={t('addDomain.registered.title', { domain })}>
              {options.map(([key, label, sub]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={choice === key}
                  className="opt opt--button"
                  disabled={busy}
                  onClick={() => {
                    setChoice(key);
                    setErr('');
                  }}
                >
                  <span className="radio" aria-hidden="true" />
                  <span>
                    <span className="lbl">{label}</span>
                    <span className="sub">{sub}</span>
                  </span>
                </button>
              ))}
            </div>
            {choice === 'transfer' && (
              <>
                <Field
                  label={t('addDomain.authCode')}
                  hint={fieldErrors.auth_code ? undefined : t('addDomain.authCode.hint')}
                  error={errorText(fieldErrors.auth_code, t)}
                >
                  <input
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={255}
                    value={authCode}
                    disabled={busy}
                    onChange={(e) => setAuthCode(e.target.value)}
                  />
                </Field>
                <RegistrantFields value={registrant} onChange={setRegistrant} errors={fieldErrors} lang={lang} disabled={busy} />
                <p className="field-hint" style={{ margin: 0 }}>{t('addDomain.vatNote')}</p>
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  const popular = (tlds || []).filter((x) => x.is_popular && x.price_register_yearly != null).slice(0, 4);

  return (
    <Modal
      as="form"
      onSubmit={next}
      onClose={onClose}
      maxWidth={480}
      title={t('dom.add')}
      description={t('addDomain.description.check')}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t('g.cancel')}
          </button>
          <button type="submit" className="btn-primary" disabled={checking}>
            {checking ? t('addDomain.checking') : t('addDomain.next')}
          </button>
        </>
      }
    >
      <div className="modal-body">
        <div className="login-form">
          <Field
            label={t('addDomain.name')}
            hint={fieldError ? undefined : t('addDomain.name.hint')}
            error={fieldError}
          >
            <input
              placeholder={t('addDomain.name.placeholder')}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="url"
              maxLength={253}
              value={input}
              onChange={(e) => changeInput(e.target.value)}
              onBlur={() => input && setTouched(true)}
            />
          </Field>
          {err && <div className="login-error" role="alert">{err}</div>}
          {availability && !availability.available && availability.status !== 'registered' && (
            <div className="login-error" role="alert">
              {t(`addDomain.status.${UNAVAILABLE.includes(availability.status) ? availability.status : 'unknown'}`, {
                domain: availability.domain || result.domain,
              })}
            </div>
          )}
          {popular.length > 0 && (
            <p className="field-hint" style={{ margin: 0 }}>
              {t('addDomain.prices', {
                list: popular.map((x) => `${x.extension} ${formatMoney(x.price_register_yearly, lang)}`).join(' · '),
              })}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
